import { UploadClient } from "./UploadClient.js";
import { uploadClientComponentShadowRoot } from "./assets.js";
import { base64toFile } from "./base64toFile.js";
import { UploadClientAttributeChangedEvent, UploadClientCancelRequestedEvent, UploadClientComponentAdoptedEvent, UploadClientComponentConnectedEvent, UploadClientComponentDisconnectedEvent, UploadClientFileClearedEvent, UploadClientFileInputChangeEvent, UploadClientFileSelectedEvent, UploadClientProgressUpdateEvent, UploadClientStateChangeEvent, UploadClientUploadCancelledEvent, UploadClientUploadCompleteEvent, UploadClientUploadErrorEvent, UploadClientUploadStartEvent, UploadClientUploadStartRequestedEvent } from "./events/UploadClientEvents.js";
import { FileDragDropHandler } from "./file-drag-drop-handler.js";
import { isThemeValid } from "./types/UploadClientComponentTypes.js";
import { formatFileSize, parseFileSize, validateFiles } from "./utils.js";
class UploadClientComponent extends HTMLElement {
    static registered = false;
    _currentFile = null;
    _currentState;
    _dragAllowed = undefined;
    _fileDragDropHandler;
    _i18n;
    _options;
    _routeCancel;
    _routeChunk;
    _routeFinish;
    _routeStart;
    _theme;
    _uploadClient;
    cancelBtn;
    currentUploadId = null;
    defaultLabelContent = "";
    defaultStatusContent = "";
    fileInput;
    fileInputLabel;
    iconExtensionEl;
    progressBarEl;
    progressContainerEl;
    /**
     * Elenco stati che il componente può assumere:
     */
    states = ["initial", "file_selected", "uploading", "completed", "error"];
    statusEl;
    uploadBtn;
    onError = (error) => {
        //done:upload-error
        this.dispatchEvent(new UploadClientUploadErrorEvent(error));
        console.log(`Errore durante l'upload: ${error}`);
        this.showStatus(error);
        this.currentState = "error";
    };
    static webComponentName = "wc-upload-client";
    constructor() {
        super();
        this.render();
        this.applyTheme();
        this.defaultLabelContent = this.i18n.selectFile;
        this.defaultStatusContent = this.i18n.statusInitial;
        if (this.options.dragEnabled) {
            this.initFileDragDropHandler(); // Inizializza il gestore drag and drop
        }
        this.internalEventListeners();
        this.currentState = "initial"; // Imposta lo stato iniziale
    }
    static get observedAttributes() {
        return ['theme'];
    }
    // GETTERS e SETTERS
    // definizione che imposta l'upload come programmatico 
    _manual;
    get manual() {
        if (this._manual === undefined) {
            this._manual = this.hasAttribute('manual') && this.getAttribute('manual') !== 'false';
        }
        return this._manual;
    }
    get currentFile() {
        return this._currentFile;
    }
    set currentFile(value) {
        if (value && !(value instanceof File)) {
            throw new TypeError("currentFile must be a File or null");
        }
        if (value) {
            //done:file-selected
            const fileSelectedEvent = new UploadClientFileSelectedEvent(value);
            this.dispatchEvent(fileSelectedEvent);
            if (fileSelectedEvent.defaultPrevented) {
                return;
            }
            else {
                this.currentState = "file_selected";
                this.evalFileType(value);
            }
        }
        this._currentFile = value;
    }
    get currentState() {
        return this._currentState;
    }
    set currentState(value) {
        this._currentState = value;
        //done:state-change
        this.dispatchEvent(new UploadClientStateChangeEvent(value));
        this.updateUI();
    }
    get dragAllowed() {
        if (this._dragAllowed === undefined) {
            this._dragAllowed = this.hasAttribute('drag-allowed') && this.getAttribute('drag-allowed') !== 'false';
        }
        return this._dragAllowed;
    }
    get i18n() {
        if (!this._i18n) {
            this._i18n = {
                cancel: 'Annulla',
                upload: 'Carica',
                selectFile: 'Seleziona file',
                uploading: 'Caricamento in corso',
                completed: 'Caricamento completato',
                error: 'Errore durante il caricamento',
                statusInitial: `Seleziona  ${this.dragAllowed ? 'o trascina' : ''} un file per iniziare`,
            };
            // Carica le traduzioni dal dataset data-i18n-*
            const i18nAttributes = this.parseDatasetGroup('i18n');
            this._i18n = {
                ...this._i18n,
                ...i18nAttributes
            };
        }
        return this._i18n;
    }
    // le opzioni sono ricavate dagli attributi
    get options() {
        if (!this._options) {
            const maxFileSizeAttr = this.getAttribute('max-size') || "1Gb";
            let maxFileSize = 0;
            try {
                maxFileSize = parseFileSize(maxFileSizeAttr);
            }
            catch (error) {
                maxFileSize = 1000 * 1024 * 1024; // 1GB default
            }
            this._options = {
                maxFileSize,
                allowedTypes: this.getAttribute('allowed-types')?.split(',').map(type => type.trim()) || [],
                maxFiles: parseInt(this.getAttribute('max-files') || '1', 10),
                multiple: false, // sempre false per il componente
                dragEnabled: !this.hasAttribute('drag-enabled') || (this.hasAttribute('drag-enabled') && this.getAttribute('drag-enabled') !== 'false'),
                dragOverClass: this.getAttribute('drag-over-class') || 'drag-over',
                dragActiveClass: this.getAttribute('drag-active-class') || 'drag-active',
                preventBodyDrop: true
            };
        }
        return this._options;
    }
    get routeCancel() {
        if (!this._routeCancel) {
            this._routeCancel = this.getAttribute('route-cancel') || '/api/upload/cancel';
        }
        return this._routeCancel;
    }
    set routeCancel(value) {
        this._routeCancel = value;
    }
    get routeChunk() {
        if (!this._routeChunk) {
            this._routeChunk = this.getAttribute('route-chunk') || '/api/upload/chunk';
        }
        return this._routeChunk;
    }
    set routeChunk(value) {
        this._routeChunk = value;
    }
    get routeFinish() {
        if (!this._routeFinish) {
            this._routeFinish = this.getAttribute('route-finish') || '/api/upload/finish';
        }
        return this._routeFinish;
    }
    set routeFinish(value) {
        this._routeFinish = value;
    }
    get routeStart() {
        if (!this._routeStart) {
            this._routeStart = this.getAttribute('route-start') || '/api/upload/start';
        }
        return this._routeStart;
    }
    set routeStart(value) {
        this._routeStart = value;
    }
    get theme() {
        if (!this._theme) {
            const themeAttr = this.getAttribute('theme') || 'default';
            if (isThemeValid(themeAttr)) {
                this._theme = themeAttr;
            }
            else {
                this._theme = 'default'; // Default theme if invalid
            }
        }
        return this._theme;
    }
    set theme(value) {
        let themeChanged = false;
        if (isThemeValid(value)) {
            themeChanged = (value !== this._theme);
            this._theme = value;
        }
        if (themeChanged) {
            this.applyTheme();
        }
    }
    get uploadClient() {
        if (!this._uploadClient) {
            this._uploadClient = UploadClient.getInstance({
                routeNames: {
                    start: this.routeStart,
                    chunk: this.routeChunk,
                    finish: this.routeFinish,
                    cancel: this.routeCancel
                },
                onLog: (message) => console.log(message),
                onStart: (uploadId) => this.onStart(uploadId),
                onProgress: (info) => this.updateProgress(info.progress),
                onComplete: (result) => this.onComplete(result),
                onError: (error) => this.onError(error),
                onCancel: () => this.showStatus('Upload cancellato', 5000)
            });
        }
        return this._uploadClient;
    }
    static registerElement() {
        if (!this.registered) {
            const elementClass = this;
            const name = elementClass.webComponentName;
            customElements.define(name, elementClass);
            this.registered = true;
        }
    }
    addEventListener(type, listener, options) {
        super.addEventListener(type, listener, options);
    }
    adoptedCallback() {
        //done:component-adopted
        this.dispatchEvent(new UploadClientComponentAdoptedEvent());
    }
    attributeChangedCallback(name, oldValue, newValue) {
        //done:attribute-changed
        this.dispatchEvent(new UploadClientAttributeChangedEvent(name, oldValue, newValue));
        if (name === 'theme') {
            this.theme = newValue;
        }
    }
    async cancelUpload() {
        //done:cancel-requested
        this.dispatchEvent(new UploadClientCancelRequestedEvent(this.currentUploadId));
        if (this.currentState === "uploading") {
            try {
                if (this.currentUploadId) {
                    console.warn("Nessun upload in corso da cancellare.");
                    await this.uploadClient.cancel(this.currentUploadId);
                }
                this.currentUploadId = null;
                this.currentFile = null;
                //done:upload-cancelled
                this.dispatchEvent(new UploadClientUploadCancelledEvent(this.currentUploadId));
                this.currentState = "initial";
            }
            catch (error) {
                this.onError(`Errore durante la cancellazione: ${error}`);
            }
        }
        else {
            console.log("Upload non in corso, nessun upload da cancellare.");
            this.currentState = "initial";
        }
    }
    connectedCallback() {
        //done:component-connected
        this.dispatchEvent(new UploadClientComponentConnectedEvent());
        console.log("Custom element added to page.");
    }
    disconnectedCallback() {
        //done:component-disconnected
        this.dispatchEvent(new UploadClientComponentDisconnectedEvent());
        console.log("Custom element removed from page.");
    }
    evalFileType(file) {
        // estraggo l'estensione
        const nameParts = file.name.split('.');
        const ext = nameParts.length > 1 ? nameParts.pop().toLowerCase() : '';
        if (this.iconExtensionEl) {
            this.iconExtensionEl.textContent = ext || 'file';
        }
    }
    hideStatus() {
        this.statusEl.textContent = '';
        this.statusEl.style.display = 'none';
    }
    onComplete(result) {
        //done:upload-success
        this.dispatchEvent(new UploadClientUploadCompleteEvent(result));
        console.log(`Upload completato: ${result.filename} (${formatFileSize(result.finalSize)})`);
        this.showStatus('Upload completato con successo!', 5000);
    }
    removeEventListener(type, listener, options) {
        super.removeEventListener(type, listener, options);
    }
    reset() {
        setTimeout(() => {
            this.currentUploadId = null;
            this.currentFile = null;
            this.hideStatus();
            if (this.fileInput) {
                this.fileInput.value = '';
            }
            if (this.progressBarEl) {
                this.progressBarEl.style.width = '0%';
                this.progressBarEl.textContent = '0%';
            }
            this.currentState = "initial";
        }, 500);
        console.log("currentState", this.currentState);
    }
    async uploadFile() {
        if (this.currentFile) {
            try {
                //done:upload-start-requested
                const uploadStartRequestedEvent = new UploadClientUploadStartRequestedEvent(this.currentFile);
                this.dispatchEvent(uploadStartRequestedEvent);
                if (uploadStartRequestedEvent.defaultPrevented) {
                    // equivale a cancellare l'upload
                    this.dispatchEvent(new UploadClientUploadCancelledEvent(this.currentUploadId));
                    this.currentState = "initial";
                    return; // Se l'evento è stato prevenuto, non procedere con l'upload
                }
                this.currentState = "uploading";
                const result = await this.uploadClient.upload(this.currentFile);
                this.currentState = "completed";
            }
            catch (error) {
                this.onError(`Errore durante l'upload: ${error}`);
                this.showStatus('Errore durante l\'upload');
            }
            finally {
                this.currentUploadId = null;
            }
        }
    }
    onStart(uploadId) {
        this.currentUploadId = uploadId;
        //done:upload-start
        this.dispatchEvent(new UploadClientUploadStartEvent(uploadId));
        this.currentState = "uploading";
    }
    render() {
        const shadow = this.attachShadow({ mode: 'open' });
        if (!shadow) {
            console.error("Shadow root not attached.");
            return;
        }
        if (this.manual) {
            this.classList.add('manual');
        }
        shadow.innerHTML = uploadClientComponentShadowRoot;
        this.fileInputLabel = shadow.getElementById('fileInputLabel');
        this.fileInput = shadow.getElementById('fileInput');
        this.uploadBtn = shadow.getElementById('uploadBtn');
        this.cancelBtn = shadow.getElementById('cancelBtn');
        this.progressContainerEl = shadow.querySelector('.progress');
        this.progressBarEl = shadow.getElementById('progressBar');
        this.statusEl = shadow.getElementById('status');
        this.iconExtensionEl = shadow.getElementById('icon-extension');
        this.setFileInputLabelText();
        // Event listeners
        this.uploadBtn?.addEventListener('click', () => this.uploadFile());
        this.cancelBtn.addEventListener('click', () => this.cancelUpload());
        this.fileInput.addEventListener('change', () => {
            if (this.fileInput.files && this.fileInput.files.length > 0) {
                //done:file-input-change
                this.currentFile = this.fileInput.files[0];
                this.dispatchEvent(new UploadClientFileInputChangeEvent(this.fileInput.files[0]));
            }
            else {
                //done:file-cleared
                this.dispatchEvent(new UploadClientFileClearedEvent());
                this.currentFile = null;
            }
        });
        // this.showStatus(this.i18n.selectFile, "warning");
        shadow.querySelectorAll('[data-i18n]').forEach((el) => {
            const key = el.getAttribute('data-i18n');
            if (key && this.i18n[key]) {
                el.textContent = this.i18n[key];
            }
        });
    }
    showStatus(message, duration = 0) {
        if (!this.statusEl) {
            console.error("Status element not found.");
            return;
        }
        this.statusEl.textContent = message;
        this.statusEl.style.display = 'block';
        if (duration > 0) {
            setTimeout(() => {
                this.hideStatus();
            }, duration);
        }
    }
    updateProgress(percentage) {
        if (!this.progressBarEl) {
            console.error("Progress bar not found.");
            return;
        }
        //done:progress-update
        this.dispatchEvent(new UploadClientProgressUpdateEvent(percentage));
        this.progressBarEl.style.display = 'block';
        this.progressBarEl.style.width = `${percentage}%`;
        this.progressBarEl.textContent = `${percentage}%`;
    }
    updateUI() {
        this.setStateClass();
        if (this.uploadBtn) {
            this.uploadBtn?.classList.toggle('hide', !(["file_selected"].includes(this.currentState)));
        }
        if (this.cancelBtn) {
            this.cancelBtn?.classList.toggle('hide', !(["file_selected", "uploading", "error"].includes(this.currentState)));
        }
        if (this.fileInput) {
            this.fileInput?.classList.toggle('hide', !(["file_selected", "uploading"].includes(this.currentState)));
            this.fileInput.disabled = this.currentState !== "initial";
        }
        if (this.progressContainerEl) {
            this.progressContainerEl?.classList.toggle('hide', !(["uploading"].includes(this.currentState)));
        }
        if (this.currentState === "initial") {
            this.setFileInputLabelText();
        }
    }
    applyTheme() {
        const theme = this.theme || 'default';
        this.classList.remove('default', 'compact');
        this.classList.add(`${theme}`);
    }
    async setFile(file, upload = false) {
        const options = this.options;
        // Valida il file come fa il drag&drop handler
        const validation = validateFiles([file], options);
        if (!validation.valid) {
            const error = validation.errors.join(', ');
            this.dispatchEvent(new UploadClientUploadErrorEvent(error));
            this.showStatus(error);
            this.currentState = "error";
            return;
        }
        // Imposta il file (triggera il setter con eventi)
        this.currentFile = file;
        // Dispatch lo stesso evento del file input change
        this.dispatchEvent(new UploadClientFileInputChangeEvent(file));
        if (upload) {
            await this.uploadFile();
        }
    }
    async setBase64File(base64String, filename, mimeType, upload = false) {
        try {
            const file = base64toFile(base64String, filename, mimeType);
            await this.setFile(file, upload);
            return true;
        }
        catch (error) {
            const errorMessage = `Errore nel convertire base64 in file: ${error instanceof Error ? error.message : 'errore sconosciuto'}`;
            this.dispatchEvent(new UploadClientUploadErrorEvent(errorMessage));
            this.showStatus(errorMessage);
            this.currentState = "error";
            return false;
        }
    }
    initFileDragDropHandler() {
        if (!this._fileDragDropHandler) {
            const self = this;
            this._fileDragDropHandler = new FileDragDropHandler(this, {
                onFileDrop: (files) => {
                    self.currentFile = files[0];
                    self.dispatchEvent(new UploadClientFileInputChangeEvent(files[0]));
                },
                onError(error, files) {
                    console.error("Drag drop error:", error, files);
                    self.dispatchEvent(new UploadClientUploadErrorEvent(error));
                    self.showStatus(error);
                },
            });
        }
        return this._fileDragDropHandler;
    }
    internalEventListeners() {
        // qui gli eventi vengono riportati per poterli gestire in modo centralizzato
        // this.addEventListener('state-change', (e: UploadClientStateChangeEvent) => {
        // });
        this.addEventListener('file-selected', (e) => {
            const file = e.data;
            if (!file)
                return;
            const size = formatFileSize(file.size);
            this.setFileInputLabelText(`${file.name} (${size})`);
        });
        // this.addEventListener('file-input-change', (e: UploadClientFileInputChangeEvent) => {
        // });
        // this.addEventListener('file-cleared', () => {
        // });
        // this.addEventListener('upload-start', (e: UploadClientUploadStartEvent) => {
        // });
        // this.addEventListener('progress-update', (e: UploadClientProgressUpdateEvent) => {
        // });
        // this.addEventListener('upload-error', (e: UploadClientUploadErrorEvent) => {
        // });
        // this.addEventListener('upload-complete', (e: UploadClientUploadCompleteEvent) => {
        // });
    }
    parseDatasetGroup(groupname) {
        const result = {};
        Array.from(this.attributes).forEach(attr => {
            const name = attr.name;
            if (name.startsWith(`data-${groupname}-`)) {
                let key = name.replace(`data-${groupname}-`, '');
                const value = attr.value;
                key = key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase(); // Convert camelCase to kebab-case
                result[key] = value;
            }
        });
        return result;
    }
    setFileInputLabelText(text = "") {
        if (this.fileInputLabel) {
            this.fileInputLabel.textContent = text || this.defaultLabelContent;
        }
        else {
            console.error("File input label not found.");
        }
    }
    setStateClass() {
        this.states.forEach(state => {
            this.classList.toggle(state, this.currentState === state);
        });
    }
}
// Export per permettere side-effect import
export default UploadClientComponent;
export { UploadClientComponent };
